import _ from "lodash";
import React, { useRef, useState, useEffect } from "react";
import { Button, Modal, SearchInput } from "@toddle-edu/ds-web";
import classes from "./ResourceBankV2.scss";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { getResourceListQueryV2 } from "../containers/Queries";
import { getResourceBankDetailsFromCache } from "../containers/ResourceBankGraphqlHelper";
import NoDataComponent from "UIComponents/NoDataComponent";
import PaginationLoader from "UIComponents/PaginationLoader";
import withLoader from "UIComponents/GqlWithLoader";
import { prepareLoadMoreFeed, navigateGridWithArrowKeys } from "Utils";
import { useHandleScroll } from "Utils/CustomHooks";
import { useI18n } from "Hooks";
import ResourceCard from "./ResourceCard";
import classNames from "classnames";
import {
  ASSESSMENT,
  SHIFT_ENTER_SUBMIT_SHORTCUT,
  ELEMENT_TYPE_UNIT_PLAN,
} from "Constants/stringConstants";

import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getUnitPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";

import ACLStore from "lib/aclStore";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getUnitPlanFieldResponseFilter } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { getUnitPlanCentralRepoInputAndFilters } from "Teacher/routes/Courses/modules/utils";

const ResourceList = props => {
  const {
    selectedResources,
    id,
    toggleSelectedResources,
    multi,
    resources,
    currentImageService,
    sourceErrorMessage,
  } = props;

  const { t } = useI18n(["common", "integration", "toastMsgs"]);

  // Refs to track each ResourceCard and the container
  const resourceCardRefs = useRef({});
  const containerRef = useRef(null);

  let containerComputedStyle;
  if (containerRef.current) {
    containerComputedStyle = window.getComputedStyle(containerRef.current);
  }

  const columnsCount = containerComputedStyle?.gridTemplateColumns?.split(" ")
    ?.length; // Calculate the number of cards per row

  // Function to move focus based on key press
  const moveFocusToNextOrPrevResourceData = (event, index) => {
    navigateGridWithArrowKeys({
      event,
      index,
      columnsCount,
      itemsCount: resources.length,
      refList: resourceCardRefs,
    });
  };

  if (sourceErrorMessage) {
    return (
      <NoDataComponent
        emptyHeaderText={t("common:no_images_at_the_moment")}
        isSearch={true}
        emptyText={t("integration:image_service_unavailable_info_with_label", {
          source: currentImageService === "PIXABAY" ? "Pixabay" : "Pexels",
        })}
      />
    );
  }

  if (!_.size(resources)) {
    return <NoDataComponent />;
  }

  return (
    <div className={classes.resourceContentContainer} ref={containerRef}>
      {_.map(resources, (resource, index) => (
        <ResourceCard
          id={id}
          key={index}
          index={index}
          resource={resource}
          selectedResources={selectedResources}
          toggleSelectedResources={toggleSelectedResources}
          currentImageService={currentImageService}
          multi={multi}
          t={t}
          resourceCardRefs={resourceCardRefs}
          onKeyDown={e => moveFocusToNextOrPrevResourceData(e, index)}
        />
      ))}
    </div>
  );
};

const ResourceBankFeedList = props => {
  const {
    resourceBankData,
    loadMoreFeed,
    hasNextPage,
    networkStatus,
    selectedResources,
    toggleSelectedResources,
    multi,
    id,
    isFetchingMoreData,
    currentImageService,
    sourceErrorMessage,
    isDsV2,
  } = props;

  const containerRef = useRef();

  const getScrollRef = () => {
    return containerRef.current;
  };

  useHandleScroll({ networkStatus, loadMoreFeed, hasNextPage, getScrollRef });

  const resourceListClasses = classNames({
    [classes.resourceListContainer]: true,
    [classes.resourceListContainerDsV2]: isDsV2,
  });

  return (
    <div ref={containerRef} className={resourceListClasses}>
      <ResourceList
        selectedResources={selectedResources}
        toggleSelectedResources={toggleSelectedResources}
        currentImageService={currentImageService}
        multi={multi}
        id={id}
        resources={resourceBankData}
        sourceErrorMessage={sourceErrorMessage}
      />
      {isFetchingMoreData ? <PaginationLoader /> : null}
    </div>
  );
};

const getResourceBankFilters = ({ searchText, currentImageService }) => {
  return {
    first: 20,
    searchEntity: [currentImageService],
    searchText,
  };
};

const getResourceBankFiltersMemoized = _.memoize(
  params => getResourceBankFilters(params),
  params => JSON.stringify(params)
);

const getCollaboratorFilters = ({ academicYearId }) => {
  return {
    userAcademicYearId: academicYearId,
  };
};

const getCollaboratorFiltersMemoized = _.memoize(
  getCollaboratorFilters,
  JSON.stringify
);

const mapStateToProps = (state, ownProps) => {
  const { searchText, currentImageService } = ownProps;
  const filters = getResourceBankFiltersMemoized({
    searchText,
    currentImageService,
  });

  return {
    isData: true,
    isLoading: false,
    filters,
  };
};

const ResourceBankFeedListWithLoader = compose(
  connect(mapStateToProps, null),
  graphql(getResourceListQueryV2, {
    name: "getResourceListV2",
    options: ({ filters }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        input: filters,
      },
    }),
    props({ getResourceListV2, ownProps: { filters, isData, isLoading } }) {
      const resourceBankDetails = getResourceBankDetailsFromCache({
        input: filters,
      });
      const resourceBankData = _.map(
        _.get(resourceBankDetails, "resourceBankSearchResultV2.edges", []),
        data => data.node
      );

      const sourceErrorMessage = _.get(
        resourceBankDetails,
        "resourceBankSearchResultV2.errorMessage",
        false
      );

      const networkStatus = _.get(getResourceListV2, "networkStatus");

      const hasNextPage = _.get(
        resourceBankDetails,
        "resourceBankSearchResultV2.pageInfo.hasNextPage",
        false
      );

      const endCursor = _.get(
        resourceBankDetails,
        "resourceBankSearchResultV2.pageInfo.endCursor",
        ""
      );

      return {
        isData: !_.isEmpty(resourceBankDetails) && isData,
        resourceBankData,
        sourceErrorMessage,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        networkStatus,
        hasNextPage,
        isFetchingMoreData: networkStatus == 3,
        loadMoreFeed: prepareLoadMoreFeed({
          variables: {
            input: {
              ...filters,
              currentPage: parseInt(endCursor),
            },
          },
          queryData: getResourceListV2,
          key: "resourceBankSearchResultV2",
          nodeKey: "platform",
        }),
      };
    },
  }),
  withLoader
)(ResourceBankFeedList);

const ResourceBankV2 = props => {
  const {
    onClose,
    selectedResources,
    toggleSelectedResources,
    multi,
    onAddClick,
    id,
    parentId,
    fieldParentType,
    fieldParentId,
    collaboratorsFilter,
    dsVersion,
    academicYearId,
    selectedCourseId,
    organizationId,
  } = props;

  const { t } = useI18n(["common"]);

  const [filter, setFilter] = useState({ searchText: "" });
  const onFilterChange = (value, key) => {
    setFilter(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffect(() => {
    // for assessment, by default search should be the title of the assessment
    if (fieldParentType === ASSESSMENT) {
      const assessmentDetails = getAssessmentDetailsFromCache({
        id: parentId,
        collaboratorsFilter,
      });

      const title = _.get(assessmentDetails, "title.value", "");
      if (!_.isEmpty(title)) {
        onFilterChange(title, "searchText");
      }
    } else if (fieldParentType === ELEMENT_TYPE_UNIT_PLAN) {
      const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
        organizationId: organizationId,
        courseId: selectedCourseId,
        academicYearId,
      });
      const responseFilter = getUnitPlanFieldResponseFilter();
      const unitPlanDetails = getUnitPlanDetailsFromCache({
        unitPlanId: fieldParentId,
        centralRepoInput,
        responseFilter,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter: collaboratorsFilter,
      });
      const title = _.get(unitPlanDetails, "title.value", "");
      if (!_.isEmpty(title) && !_.isEqual(title, t("common:untitled"))) {
        onFilterChange(title, "searchText");
      }
    }
  }, [
    parentId,
    fieldParentType,
    collaboratorsFilter,
    academicYearId,
    selectedCourseId,
    organizationId,
    fieldParentId,
  ]);

  const isDsV2 = dsVersion === "2.0";

  const resourceSelectedText = classNames({
    ["text-label-3"]: !isDsV2,
    [classes.footerSelectedText]: isDsV2,
    ["text-label"]: isDsV2,
  });

  const isPixabayFeatureFlagEnabled = ACLStore.can(
    "FeatureFlag:getImagesFromPixabay"
  );

  const isPixxelFeatureFlagEnabled = ACLStore.can(
    "FeatureFlag:getImagesFromPexels"
  );

  const currentImageService = isPixabayFeatureFlagEnabled
    ? "PIXABAY"
    : isPixxelFeatureFlagEnabled
    ? "PEXELS"
    : "UNSPLASH";

  const modalHeader = t("common:add_image");

  const footerButtonContainerClasses = classNames({
    [classes.footerButtonContainer]: true,
    [classes.footerButtonContainerDsV2]: isDsV2,
  });

  return (
    <Modal
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      hasOverlay={true}
      onClose={onClose}
      width="67vw"
      height="90vh"
      ResourceBankV2
      dsVersion={dsVersion}
    >
      <Modal.Header title={modalHeader} hasSeparator={isDsV2} />
      <Modal.Body isScrollable={true}>
        <SearchInput
          value={filter.searchText}
          placeholder={t("common:search_placeholder")}
          onChange={value => onFilterChange(value, "searchText")}
          size={isDsV2 ? "medium" : "large"}
          focusSearchBySlash={isDsV2}
          dsVersion={dsVersion}
        />
        <ResourceBankFeedListWithLoader
          searchText={filter.searchText}
          selectedResources={selectedResources}
          toggleSelectedResources={toggleSelectedResources}
          currentImageService={currentImageService}
          multi={multi}
          id={id}
          isDsV2={isDsV2}
        />
      </Modal.Body>
      <Modal.Footer hasSeparator={isDsV2}>
        <div className={classes.footer}>
          <div className={resourceSelectedText}>
            {t("common:count_selected", {
              count: _.size(selectedResources),
            })}
          </div>
          <div className={footerButtonContainerClasses}>
            <Button
              variant={isDsV2 ? "neutral" : "outlined-subtle"}
              size={isDsV2 ? "medium" : "large"}
              onClick={onClose}
              dsVersion={dsVersion}
              type={isDsV2 ? "outlined" : ""}
            >
              {t("common:cancel")}
            </Button>
            <Button
              variant={isDsV2 ? "primary" : "progressive"}
              size={isDsV2 ? "medium" : "large"}
              disabled={!_.size(selectedResources)}
              onClick={onAddClick}
              dsVersion={dsVersion}
              type={isDsV2 ? "fill" : ""}
              shortcut={SHIFT_ENTER_SUBMIT_SHORTCUT}
            >
              {t("common:add")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const resourceBankMapStateToProps = state => {
  const academicYearId = getSelectedAcademicYearId(state);
  const collaboratorsFilter = getCollaboratorFiltersMemoized({
    academicYearId,
  });
  const organizationId = _.get(state, "login.userInfo.org_id", "");
  const selectedCourseId = _.get(
    state,
    "teacher.selected_class.selected_course",
    ""
  );
  return {
    collaboratorsFilter,
    academicYearId,
    selectedCourseId,
    organizationId,
  };
};

export default connect(resourceBankMapStateToProps, null)(ResourceBankV2);
